import {
  Checkbox,
  CircularProgress,
  IconButton,
  Popper,
  PopperProps,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { memoize } from 'lodash';
import {
  Autocomplete,
  createFilterOptions,
  FilterOptionsState,
} from '@material-ui/lab';
import { untree, getLevel } from 'helpers/untree';
import React from 'react';
import ListboxComponent from 'components/atoms/ListboxComponent';
import useStyles from './style';
import {
  IAutocompleteMultipleCheckboxTreeProps,
  IAutocompleteMultipleCheckboxTreeRefProps,
  IOption,
  IOptionId,
} from './types';

const AutocompleteMultipleCheckboxTree = React.forwardRef<
  IAutocompleteMultipleCheckboxTreeRefProps,
  IAutocompleteMultipleCheckboxTreeProps
>(
  (
    {
      id,
      name,
      label,
      placeholder,
      items,
      loading,
      selectAllLabel,
      noOptionsText,
      disableClearable = false,
      initialState = [],
      onFilterChange,
      toggleState,
    },
    ref,
  ) => {
    const classes = useStyles();
    const scrollRef = React.useRef<number | null>(0);

    const [expanded, setExpanded] = React.useState<IOptionId[]>([]);
    const [selectedOptions, setSelectedOptions] = React.useState<IOption[]>([]);
    const [open, setOpen] = React.useState<boolean>(false);
    const [inputValue, setInputValue] = React.useState<string>('');

    const selectedOptionsIds = React.useMemo(
      () => selectedOptions.map(item => item.id),
      [selectedOptions],
    );

    React.useEffect(() => {
      setSelectedOptions([]);
    }, [toggleState]);

    const isDisabled = React.useMemo(
      () => items?.length === 0 && !inputValue && !open,
      [inputValue, items?.length, open],
    );

    const disableExpandIconButton = React.useMemo(() => {
      return Boolean(inputValue.length);
    }, [inputValue.length]);

    const onlyAvaliableOptions = React.useMemo<IOption[]>(
      () =>
        selectedOptions.filter(sItem =>
          items.some(fItem => fItem.id === sItem.id),
        ),
      [items, selectedOptions],
    );

    const hasMoreOneSelected = React.useMemo(
      () => selectedOptions.length > 1,
      [selectedOptions.length],
    );

    const handleToggleOption = React.useCallback(
      (toggleOptions: IOption[]) => setSelectedOptions(toggleOptions),
      [],
    );

    const handleClearOptions = React.useCallback(
      () => setSelectedOptions([]),
      [],
    );

    const getLimitTagsText = (more: number) =>
      `${more} ${hasMoreOneSelected ? 'Itens' : 'Item'}`;

    const getOptionDisabled = React.useCallback((option: IOption) => {
      return Boolean(option?.disabled);
    }, []);

    const getOptionLabel = React.useCallback((option: IOption) => {
      return option.description;
    }, []);

    const getOptionSelected = React.useCallback(
      (option: IOption, value: IOption) => {
        return option.id === value.id;
      },
      [],
    );

    const MyPopper = React.useCallback(
      (popperProps: PopperProps) => (
        <Popper
          {...popperProps}
          style={{ maxWidth: '500px', overflowY: 'auto', overflowX: 'hidden' }}
          placement="bottom-start"
        />
      ),
      [],
    );

    const inputRenderer = React.useCallback(
      (params: TextFieldProps) => (
        <TextField
          variant="outlined"
          {...params}
          label={label}
          placeholder={placeholder}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    aria-label="autocomplete-loading"
                    color="inherit"
                    size={25}
                  />
                ) : null}
                {params?.InputProps?.endAdornment}
              </>
            ),
          }}
        />
      ),
      [loading, label, placeholder],
    );

    const getItemLevel = React.useCallback(
      (option: IOption) => {
        return getLevel(option, items, 0);
      },
      [items],
    );

    const expandAll = React.useMemo(() => {
      return Boolean(inputValue.length);
    }, [inputValue.length]);

    const isExpanded = React.useCallback(
      (optionId: IOptionId) => {
        return expandAll || expanded.includes(optionId);
      },
      [expandAll, expanded],
    );

    const getRecursively = React.useCallback(
      (itemTree: IOption[]): IOption[] => untree(itemTree),
      [],
    );

    const getRecursivelyMemoize = memoize(getRecursively);

    const getRecursivelyExpandedItems = React.useCallback(
      (itemTree: IOption[]): IOption[] => {
        const extraCondition = (item: IOption) => isExpanded(item.id);
        return untree(itemTree, extraCondition);
      },
      [isExpanded],
    );

    const getRecursivelyItems = React.useCallback(
      (option: IOption) => {
        return getRecursivelyExpandedItems([option]);
      },
      [getRecursivelyExpandedItems],
    );

    const recursiveItems = React.useMemo(() => {
      return getRecursivelyExpandedItems(items);
    }, [getRecursivelyExpandedItems, items]);

    const recursiveAllItems = React.useMemo(() => {
      return getRecursively(items);
    }, [getRecursively, items]);

    const filteredItems = React.useMemo(() => {
      if (inputValue.length) {
        const result = recursiveItems.filter(item => {
          return getOptionLabel(item)
            .toLocaleLowerCase()
            .includes(inputValue.toLocaleLowerCase());
        });

        return result;
      }

      return recursiveItems;
    }, [getOptionLabel, inputValue, recursiveItems]);

    const isSelectAllInterminate: boolean = React.useMemo(() => {
      let sourceItemsLength = recursiveAllItems.length;

      if (inputValue.length) {
        sourceItemsLength = filteredItems.length;
      }

      return (
        selectedOptions.length > 0 && selectedOptions.length < sourceItemsLength
      );
    }, [
      filteredItems.length,
      inputValue.length,
      recursiveAllItems.length,
      selectedOptions.length,
    ]);

    const allSelected: boolean = React.useMemo(
      () =>
        Boolean(selectedOptions.length) &&
        selectedOptions.length === filteredItems?.length,
      [filteredItems?.length, selectedOptions.length],
    );

    const handleSelectAll = React.useCallback(
      (isSelected: boolean) => {
        if (isSelected) {
          setSelectedOptions(filteredItems);
        } else {
          handleClearOptions();
        }
      },
      [filteredItems, handleClearOptions],
    );

    const computedInputValue = React.useMemo(() => {
      if (open) {
        return inputValue;
      }

      return '';
    }, [inputValue, open]);

    const handleToggleTree = React.useCallback(
      // eslint-disable-next-line @typescript-eslint/ban-types
      (event: React.MouseEvent<{}>, nodeId: IOptionId) => {
        event.preventDefault();
        event.stopPropagation();

        if (isExpanded(nodeId)) {
          setExpanded(currentValues =>
            currentValues.filter(item => item !== nodeId),
          );
        } else {
          setExpanded(currentValues =>
            Array.from(new Set([...currentValues, nodeId])),
          );
        }
      },
      [isExpanded],
    );

    const optionRenderer = React.useCallback(
      (option: IOption, { selected }: { selected: boolean }) => {
        const TreeIcon = isExpanded(option.id)
          ? ExpandMoreIcon
          : ChevronRightIcon;

        const extraProps: { checked?: boolean; indeterminate?: boolean } = {
          checked: selected,
        };
        if (option.id === 'select-all') {
          // To control the state of 'select-all' checkbox
          extraProps.checked = allSelected;
          // To control the state of 'select-all' checkbox
          extraProps.indeterminate = isSelectAllInterminate;
        } else if (!option.isExternal) {
          const itemTree = getRecursivelyMemoize([option]).map(item => item.id);
          const itemSelectedTree = itemTree.filter(item =>
            selectedOptionsIds.includes(item),
          );

          extraProps.indeterminate =
            itemSelectedTree.length > 0 &&
            itemSelectedTree.length < itemTree.length;
        }

        const itemLevel = !option.isExternal ? getItemLevel(option) : 0;
        const canShowIcon =
          !option.isExternal && (option?.children || []).length > 0;

        const spacingBefore = 8 * itemLevel;

        return (
          <>
            {canShowIcon ? (
              <IconButton
                disabled={disableExpandIconButton}
                style={{
                  marginLeft: spacingBefore,
                }}
                onClick={event => handleToggleTree(event, option.id)}
              >
                <TreeIcon />
              </IconButton>
            ) : (
              <div style={{ width: `${48 + spacingBefore}px` }} />
            )}
            <Checkbox
              color="primary"
              aria-label={`${option.id}`}
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              onClick={event => {
                if (!option.isExternal) {
                  const parentAndChildrens = getRecursivelyItems(option);
                  const parentAndChildrensIds = parentAndChildrens.map(
                    item => `${item.id}`,
                  );

                  if (selected) {
                    // removendo os items filhos
                    setSelectedOptions(currentOptions =>
                      currentOptions.filter(
                        item => !parentAndChildrensIds.includes(`${item.id}`),
                      ),
                    );
                  } else {
                    // adicionando o pai e os filhos ao set de items
                    setSelectedOptions(currentOptions => [
                      // primeiro eu os removo para garantir que não vou adicionar items repetidos
                      ...currentOptions.filter(
                        item => !parentAndChildrensIds.includes(`${item.id}`),
                      ),
                      // em seguida só adiciona-los novamente
                      ...parentAndChildrens,
                    ]);
                  }

                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              style={{ marginRight: 8 }}
              {...extraProps}
            />
            {getOptionLabel(option)}
          </>
        );
      },
      [
        allSelected,
        disableExpandIconButton,
        getItemLevel,
        getOptionLabel,
        getRecursivelyItems,
        getRecursivelyMemoize,
        handleToggleTree,
        isExpanded,
        isSelectAllInterminate,
        selectedOptionsIds,
      ],
    );

    const filter = createFilterOptions<IOption>({
      matchFrom: 'any',
      limit: 50,
      trim: true,
    });

    const renderFilteredOptions = React.useCallback(
      (options: IOption[], params: FilterOptionsState<IOption>) => {
        const filtered = params.inputValue ? filter(options, params) : options;

        filtered.unshift({
          description: selectAllLabel,
          id: 'select-all',
          isExternal: true,
        });

        return filtered;
      },
      [selectAllLabel, filter],
    );

    const handleToggleSelectAll = React.useCallback(
      (isAllSelected: boolean) => {
        handleSelectAll(!isAllSelected);
      },
      [handleSelectAll],
    );

    const handleChange = React.useCallback(
      (
        event: React.ChangeEvent<Record<string, unknown>>,
        currentOptions: IOption[],
        reason: string,
      ) => {
        if (reason === 'select-option' || reason === 'remove-option') {
          if (currentOptions.find(option => option.id === 'select-all')) {
            handleToggleSelectAll(allSelected);
          } else {
            handleToggleOption(currentOptions);
          }
        } else if (reason === 'clear') {
          handleClearOptions();
        }
      },
      [
        handleToggleSelectAll,
        allSelected,
        handleToggleOption,
        handleClearOptions,
      ],
    );

    const renderTags = React.useCallback(
      (value: IOption[]) => value.map(option => <span key={option.id} />),
      [],
    );

    const handleInputChange = React.useCallback(
      (
        event: React.ChangeEvent<Record<string, unknown>>,
        newInputValue: string,
        reason: string,
      ) => {
        if (reason === 'input') {
          setInputValue(newInputValue);
        }
      },
      [],
    );

    const handleToggleList = React.useCallback((mode: 'OPEN' | 'CLOSE') => {
      if (mode === 'OPEN') return () => setOpen(true);

      return () => {
        setOpen(false);
      };
    }, []);

    const clearFilter = React.useCallback(
      () => setSelectedOptions(initialState),
      [initialState],
    );

    React.useImperativeHandle(ref, () => ({
      clearFilter,
    }));

    React.useEffect(() => {
      /**
       * Toda alteração dos valores selecionados
       * devem ser refletidas no onFilterChande.
       */
      onFilterChange({
        name,
        value: selectedOptions.map(item => ({
          label: item.description,
          value: item.id,
        })),
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, selectedOptions]);

    React.useEffect(() => {
      /**
       * atualiza a lista de valores selecionados
       * com base no resultado filtro nas opções selecionadas
       * dentro das opções disponíveis.
       */
      if (
        items.length > 0 &&
        selectedOptions.length !== onlyAvaliableOptions.length
      ) {
        setSelectedOptions(onlyAvaliableOptions);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    React.useEffect(() => {
      /**
       * Caso ocorra alteração nos items,
       * a refernencia da posição da lista
       * é resetada.
       */
      scrollRef.current = 0;
    }, [items]);

    return (
      <Autocomplete
        id={id}
        disabled={isDisabled}
        open={open}
        onOpen={handleToggleList('OPEN')}
        onClose={handleToggleList('CLOSE')}
        inputValue={computedInputValue}
        onInputChange={handleInputChange}
        limitTags={0}
        multiple
        className={classes.root}
        size="small"
        disableCloseOnSelect
        disableListWrap
        options={recursiveItems}
        value={selectedOptions}
        renderTags={renderTags}
        PopperComponent={MyPopper}
        getLimitTagsText={getLimitTagsText}
        noOptionsText={noOptionsText}
        ListboxProps={{
          style: { overflowY: 'hidden' },
          scrollRef,
        }}
        disableClearable={disableClearable}
        ListboxComponent={ListboxComponent}
        getOptionDisabled={getOptionDisabled}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        filterOptions={renderFilteredOptions}
        renderOption={optionRenderer}
        renderInput={inputRenderer}
      />
    );
  },
);

export default AutocompleteMultipleCheckboxTree;
